import { Loader2 } from "lucide-react";
import FilePreview from "@/modern/components/file-preview";
import { useFileUrl } from "@/react/components/entityViews/files/api/files.api";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { cn, getFileExtension } from "@/modern/utils";

const VIDEO_EXTENSIONS = ["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "m4v"];

const looksLikeImageUrl = (url: string) =>
  /\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i.test(url);

/** Resolve display src via GET /file/{id}/presigned-url (blob URL for optimistic uploads). */
export function useFileDisplaySrc(
  file: SwivuFile | null | undefined,
  options?: { preferThumbnail?: boolean; forVideoThumbnail?: boolean },
) {
  const preferThumbnail = options?.preferThumbnail ?? true;
  const forVideoThumbnail = options?.forVideoThumbnail ?? false;
  // List url/thumbnailUrl are not media srcs — always resolve via presign.
  const fileId = file && !file.isOptimistic ? file.id : null;
  const { url, displayUrl, isLoading, isError } = useFileUrl(fileId);

  let resolved: string | null | undefined;
  if (forVideoThumbnail) {
    // Prefer a real still when present; otherwise full media URL for <video> frame capture.
    resolved =
      displayUrl && looksLikeImageUrl(displayUrl) ? displayUrl : url ?? displayUrl;
  } else {
    resolved = preferThumbnail ? displayUrl : url ?? displayUrl;
  }

  const src = file?.isOptimistic ? file.url : resolved ?? undefined;
  return {
    src,
    isLoading: fileId != null && isLoading && !src,
    isError,
  };
}

interface PresignedFilePreviewProps {
  file: SwivuFile;
  className?: string;
  enableVideoAudioRendering?: boolean;
  /** When true, show a compact spinner for grid/list thumbs instead of a tall empty state. */
  compactLoading?: boolean;
  isThumbnail?: boolean;
  /**
   * Prefer thumbnail_url from the presigned response (default true).
   * Set false for large tiles so the full file URL is used instead of a soft thumbnail.
   */
  preferThumbnail?: boolean;
  disableLoadingState?: boolean;
  fileIconClassName?: string;
  fileIconContainerClassName?: string;
  /** Show Uploading/Loading caption under the spinner (default true for compact). */
  showLoadingLabel?: boolean;
}

/**
 * FilePreview that resolves display URL via GET /file/{id}/presigned-url.
 */
export const PresignedFilePreview = ({
  file,
  className,
  enableVideoAudioRendering,
  compactLoading = false,
  isThumbnail = false,
  preferThumbnail = true,
  disableLoadingState = true,
  fileIconClassName,
  fileIconContainerClassName,
  showLoadingLabel = true,
}: PresignedFilePreviewProps) => {
  const forVideoThumbnail =
    isThumbnail && VIDEO_EXTENSIONS.includes(getFileExtension(file.name));
  const { src, isLoading } = useFileDisplaySrc(file, {
    preferThumbnail,
    forVideoThumbnail,
  });

  if (file.isOptimistic || isLoading) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-1.5 text-muted-foreground",
          compactLoading ? "w-full h-full bg-muted" : "w-full h-full min-h-[12rem]",
          className,
        )}
      >
        <Loader2 className={cn("animate-spin", compactLoading ? "size-5" : "size-6")} />
        {showLoadingLabel && file.isOptimistic && <span className="text-[10px]">Uploading...</span>}
        {showLoadingLabel && !file.isOptimistic && compactLoading && (
          <span className="text-[10px]">Loading...</span>
        )}
      </div>
    );
  }

  return (
    <FilePreview
      file={file}
      src={src}
      className={className}
      showName={false}
      openFileOnClick={false}
      disableLoadingState={disableLoadingState}
      enableVideoAudioRendering={enableVideoAudioRendering}
      isThumbnail={isThumbnail}
      fileIconClassName={fileIconClassName}
      fileIconContainerClassName={fileIconContainerClassName}
    />
  );
};
